import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Grid, List, Filter, ChevronDown, Loader2 } from 'lucide-react'
import { ContentCard, ContentItem } from './ContentCard'

export type { ContentItem } from './ContentCard'

interface ContentLibraryProps {
  onContentClick?: (content: ContentItem) => void
  onEdit?: (content: ContentItem) => void
  onDelete?: (content: ContentItem) => void
}

interface PaginationInfo {
  page: number
  limit: number
  totalCount: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export function ContentLibrary({ onContentClick, onEdit, onDelete }: ContentLibraryProps) {
  const [content, setContent] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter states
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<string>('all')
  const [showFilters, setShowFilters] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)

  // Get access token
  const accessToken = localStorage.getItem('token')

  // Get user for role-based filtering options
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  const contentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'presentation', label: 'Presentations' },
    { value: 'pdf', label: 'PDFs' },
    { value: 'image', label: 'Images' },
    { value: 'video', label: 'Videos' },
    { value: 'markdown', label: 'Markdown' }
  ]

  const accessLevels = [
    { value: 'all', label: 'All Access Levels' },
    { value: 'demo', label: 'Demo' },
    { value: 'company', label: 'Company' },
    { value: 'admin', label: 'Admin' }
  ]

  // Only show access level filter for company admins and super admins
  const canFilterByAccessLevel = user && (
    user.role === 'company_admin' ||
    user.role === 'super_admin'
  )

  const fetchContent = async () => {
    if (!accessToken) {
      setError('Not authenticated')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Build query params
      const params = new URLSearchParams()
      params.append('page', currentPage.toString())
      params.append('limit', '12')

      if (selectedType !== 'all') {
        params.append('type', selectedType)
      }

      if (canFilterByAccessLevel && selectedAccessLevel !== 'all') {
        params.append('accessLevel', selectedAccessLevel)
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim())
      }

      const response = await fetch(
        `http://localhost:3000/api/content?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to fetch content')
      }

      const data = await response.json()
      setContent(data.content)
      setPagination(data.pagination)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch content')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContent()
  }, [currentPage, selectedType, selectedAccessLevel])

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page when searching
      fetchContent()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {pagination ? `${pagination.totalCount} item${pagination.totalCount !== 1 ? 's' : ''}` : 'Loading...'}
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 border border-border rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'grid'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-secondary'
            }`}
            aria-label="Grid view"
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-secondary'
            }`}
            aria-label="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by title or description..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Filter Button */}
        <div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors"
          >
            <Filter className="w-4 h-4 text-foreground" />
            <span>Filters</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform text-foreground ${
                showFilters ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Filter Dropdown */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4 p-4 border border-border rounded-lg bg-card space-y-4"
              >
                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Content Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {contentTypes.map(type => (
                      <button
                        key={type.value}
                        onClick={() => setSelectedType(type.value)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          selectedType === type.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Access Level Filter */}
                {canFilterByAccessLevel && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Access Level
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {accessLevels.map(level => (
                        <button
                          key={level.value}
                          onClick={() => setSelectedAccessLevel(level.value)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            selectedAccessLevel === level.value
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-destructive/10 text-destructive rounded-lg border border-destructive/20">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && content.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {searchQuery || selectedType !== 'all' || selectedAccessLevel !== 'all'
              ? 'No content matches your filters'
              : 'No content available yet'}
          </p>
        </div>
      )}

      {/* Content Grid/List */}
      <AnimatePresence mode="wait">
        {!loading && !error && content.length > 0 && (
          <motion.div
            key={`${viewMode}-${currentPage}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                : 'space-y-4'
            }
          >
            {content.map(item => (
              <ContentCard
                key={item.id}
                content={item}
                onClick={onContentClick}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination */}
      {!loading && !error && pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className="px-4 py-2 border border-border rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum
              if (pagination.totalPages <= 5) {
                pageNum = i + 1
              } else if (currentPage <= 3) {
                pageNum = i + 1
              } else if (currentPage >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i
              } else {
                pageNum = currentPage - 2 + i
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    pageNum === currentPage
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className="px-4 py-2 border border-border rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
